/**
 * Rewrites mapped parent auth statuses on later-DOS sibling CPT payloads.
 * @module applySiblingStatusMappingsForLaterDos
 */
import { JCodeDetails } from "../../../../../data-model/medicalPaOrdersModel";
import {
  CPT_SIBLING_STATUS_MAPPINGS,
  CptSiblingStatusMapping,
} from "./cptSiblingStatusMappings";
import { isLaterDateOfService } from "./isLaterDateOfService";

/**
 * Maps configured parent statuses to sibling statuses on later-DOS targets.
 * Parent order rows are never rewritten.
 *
 * @param jCodes - Remapped J-code rows for the target order
 * @param isParentOrder - Whether the target is the parent order
 * @param parentDateOfService - Parent order date of service
 * @param targetDateOfService - Target order date of service
 * @param mappings - Parent → sibling status map entries
 * @returns J-code rows with sibling statuses applied on later-DOS siblings only
 */
export const applySiblingStatusMappingsForLaterDos = (
  jCodes: JCodeDetails[],
  isParentOrder: boolean,
  parentDateOfService: string | undefined,
  targetDateOfService: string | undefined,
  mappings: CptSiblingStatusMapping[] = CPT_SIBLING_STATUS_MAPPINGS,
): JCodeDetails[] => {
  if (
    isParentOrder ||
    !isLaterDateOfService(parentDateOfService, targetDateOfService)
  ) {
    return jCodes;
  }

  const siblingStatusByCurrentStatus = new Map(
    mappings.map((mapping) => [mapping.current_status, mapping.sibling_status]),
  );

  return jCodes.map((jCodeRow) => {
    const siblingStatus = siblingStatusByCurrentStatus.get(
      jCodeRow.auth_status ?? "",
    );
    if (!siblingStatus) {
      return jCodeRow;
    }

    return {
      ...jCodeRow,
      auth_status: siblingStatus,
    };
  });
};
